import { ReactElement, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getLineCoordinateSystem } from '@editorUtils'
import { DrawControllerWrapper, ShapeMesh } from '@scene'
import { find, some } from 'lodash-es'
import { v4 as uuid } from 'uuid'
import { useTheme } from '@mui/material/styles'
import { DEFAULT_LINE_LIKE_SETTINGS } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import {
  DraggableLine,
  DraggableLineRef,
  DrawControllerRef,
  ImmutableVector3,
  LineHandles,
  TransientDrawState,
  getOrientedWorldNormalsForIntersection,
} from '@modugen/ui-toolkit'
import {
  roofStoreyKey,
  useEditElementStore,
  useControlStore,
  useModelStore,
  useTapelineStore,
} from '@editorStores'
import { useClickActionEnabled } from '@editorHooks'
import { useStructuralPlanningDrawerEsc } from '@structuralPlanningHooks'
import useWallSnapTargets from 'src/components/pages/Editor/pages/StructuralPlanning/components/FloorplanDrawer/hooks/useModelSnapTargets'
import useTapelineCentersSnapTargets from 'src/components/scene/hooks/useTapelineCentersSnapTargets'
import useTapelineSnapTargets from 'src/components/scene/hooks/useTapelineSnapTargets'
import { purlinEqualnessFactor } from '../PurlinForm/schema'
import Purlin3D, { PurlinElements } from './components/Purlin3D'

interface TransientLineState {
  lineRef?: RefObject<DraggableLineRef>
}

const minPurlinLength = 0.1

interface Props {
  reset: () => void
}

const PurlinDrawer3D = ({ reset }: Props): ReactElement => {
  const { scenePalette } = useTheme()

  const drawControllerRef = useRef<DrawControllerRef>(null)
  const lineRef = useRef<DraggableLineRef>(null)

  const typeVisibility = useModelStore(state => state.visibilityByType)
  const visibleStoreys = useModelStore(state => state.visibleStoreys)

  const model = useModelStore(state => state.model)
  const addPurlin = useModelStore(state => state.addPurlin)
  const removePurlin = useModelStore(state => state.removePurlin)
  const activeElement = useEditElementStore(state => state.activeElement)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const setSnapToAngles = useControlStore(state => state.setSnapToAngles)
  const setIsDrawingActive = useControlStore(state => state.setIsDrawingActive)
  const setSnappingFeaturesEnabled = useControlStore(state => state.setSnappingFeaturesEnabled)

  const setAdditionalSnapTargets = useTapelineStore(state => state.setAdditionalSnapTargets)
  const isTapelineActive = useTapelineStore(state => state.isActive)

  const [showTempLine, setShowTempLine] = useState(false)

  const transientLineState = useRef<TransientLineState>({})

  const localPurlin = useMemo(() => find(model.purlins, 'is_local'), [model.purlins])

  const wallLineTargets = useWallSnapTargets({})

  const tapelineTargets = useTapelineSnapTargets()
  const tapelineCenterTargets = useTapelineCentersSnapTargets()

  const snapTargets = useMemo(
    () => [...wallLineTargets, ...tapelineTargets, ...tapelineCenterTargets],
    [wallLineTargets, tapelineTargets, tapelineCenterTargets],
  )

  // EFFECTS

  useEffect(() => {
    setAdditionalSnapTargets(snapTargets)

    return () => {
      setAdditionalSnapTargets(undefined)
    }
  }, [snapTargets])

  useEffect(() => {
    setIsDrawingActive(true)

    setSnapToAngles(true)

    return () => setIsDrawingActive(false)
  }, [])

  useEffect(() => {
    if (!isTapelineActive) {
      setSnappingFeaturesEnabled({
        snapToCornersAndEdges: true,
        snapOrthogonal: true,
        snapToAngle: true,
        snapToTapelineCenters: true,
        snapToComments: false,
      })
    }

    return () => {
      setSnappingFeaturesEnabled({
        snapOrthogonal: true,
        snapToCornersAndEdges: true,
        snapToAngle: true,
        snapToTapelineCenters: true,
        snapToComments: false,
      })
    }
  }, [isTapelineActive, setSnappingFeaturesEnabled])

  // EVENTS

  const onDrawStart = useCallback((state: TransientDrawState) => {
    const { drawPoint, drawTarget } = state

    if (lineRef.current && drawPoint) {
      setShowTempLine(true)

      lineRef.current?.setHandleAndStartDragging(LineHandles.End, drawPoint, drawPoint)
      transientLineState.current.lineRef = lineRef

      // the angle snapping origin needs to be defined outside of the draw
      // controller to allow for flexibility (see onLineEditStart)

      if (drawPoint && drawTarget) {
        if (snapToAngles) {
          // define the plane angular snapping needs to work inside
          const snappingNormals = getOrientedWorldNormalsForIntersection(drawTarget, true)

          if (snappingNormals) {
            drawControllerRef.current?.setAngleSnappingOrigin({
              origin: drawPoint,
              ...snappingNormals,
            })
          }
        }
      }
    }
  }, [])

  const onDrawEnd = () => {
    setShowTempLine(false)

    const { lineRef } = transientLineState.current

    if (lineRef?.current) {
      const drawnLine = lineRef.current.stopDraggingAndGetLine()

      const zStart = drawnLine.start.z
      const zEnd = drawnLine.end.z

      const isHorizontal =
        zStart.toFixed(purlinEqualnessFactor) === zEnd.toFixed(purlinEqualnessFactor)

      // in order to place the purlin on the exact same height and therefore
      // prevent any errors in the backend we have to move the end to the same
      // height if both ends are similar to each other
      const zEndFinal = isHorizontal ? drawnLine.start.z : drawnLine.end.z

      if (drawnLine.start.distanceTo(drawnLine.end) >= minPurlinLength) {
        const start = drawnLine.start
        const end = new ImmutableVector3(drawnLine.end.x, drawnLine.end.y, zEndFinal)

        const purlin: PlanLineElement = {
          ...DEFAULT_LINE_LIKE_SETTINGS,
          guid: uuid(),
          shape: {
            start,
            end,
          },
          storey: roofStoreyKey,
          is_local: true,
          coordinate_system: getLineCoordinateSystem(start, end),
          element_type: 'purlins' as ElementTypes,
        }

        addPurlin(purlin)
        setActiveElement(purlin.guid)
        transientLineState.current = {}
      }
    }
  }

  const onDrawMouseMove = useCallback((state: TransientDrawState) => {
    if (state.drawPoint && transientLineState.current.lineRef) {
      lineRef.current?.updateActiveHandle(state.drawPoint)
    }
  }, [])

  useStructuralPlanningDrawerEsc(() => {
    if (showTempLine) {
      setShowTempLine(false)
      drawControllerRef.current?.abortDrawing()
      drawControllerRef.current?.setAngleSnappingOrigin(undefined)
      transientLineState.current = {}
    } else reset()
  }, showTempLine)

  const drawControllerEnabled = useClickActionEnabled()

  return (
    <>
      {/* REDRAW HIDDEN ROOF SLABS */}
      {model.roof_slabs.map(roof_slab => {
        return (
          <ShapeMesh
            key={roof_slab.guid}
            data={roof_slab}
            translucent
            shapeColor={scenePalette.elements3d.vertical_roof_slabs}
            noPointerInteractions
            meshMaterialProps={{
              opacity: 0.1,
              transparent: true,
            }}
            visible={typeVisibility.roof_slabs && visibleStoreys.has(roofStoreyKey)}
          />
        )
      })}

      {/* RENDER PREVIOUSLY HIDDEN PURLINS */}

      {model.purlins.map(purlin => (
        <Purlin3D
          key={purlin.guid}
          purlin={purlin}
          isActive={activeElement === purlin.guid}
          onClick={() => {
            if (localPurlin) removePurlin(localPurlin.guid)
            setActiveElement(purlin.guid)
          }}
        />
      ))}

      {/* DRAWING INTERACTION */}

      <DrawControllerWrapper
        ref={drawControllerRef}
        enabled={drawControllerEnabled}
        snapToCornersAndEdges={snapToCornersAndEdges}
        snapToAngles={snapToAngles}
        enableIndicator
        additionalSnapTargets={snapTargets}
        onDrawStart={onDrawStart}
        onMouseMove={onDrawMouseMove}
        onDrawEnd={onDrawEnd}
        isValidDrawStart={intersections => {
          return !some(
            intersections,
            intersection => intersection.object.name === PurlinElements.Line,
          )
        }}
      />

      {/* TEMP LINE */}

      <DraggableLine
        ref={lineRef}
        line={{ start: new ImmutableVector3(0, 0, 1000), end: new ImmutableVector3(0, 0, 1001) }}
        lineName={PurlinElements.Line}
        handleName={PurlinElements.Handle}
        color={scenePalette.elements3d.beams as string}
        showIndicators={false}
        isVisible={showTempLine}
      />
    </>
  )
}

export default PurlinDrawer3D
